// KHIYARSA PATCH: ephemeral annotation mode ("شرح") for the freedraw tool.
// While the pointer is down, the stroke is generated by the exact same
// outline pipeline a real freedraw element uses (newFreeDrawElement with the
// current draw-tool options → getFreeDrawSvgPath), rendered as a filled path
// on the SVG trail overlay. On lift the finished path fades out. Strokes
// never become scene elements, so they cannot enter undo history, saves, or
// exports.
import {
  DEFAULT_STROKE_STREAMLINE,
  DEFAULT_STROKE_STREAMLINE_PRECISE,
  SVG_NS,
  THEME,
  applyDarkModeFilter,
  easeOut,
  getStrokeWidthByKey,
  sceneCoordsToViewportCoords,
} from "@excalidraw/common";
import { getFreeDrawSvgPath, newFreeDrawElement } from "@excalidraw/element";
import { pointFrom } from "@excalidraw/math";

import type {
  ExcalidrawFreeDrawElement,
  NonDeleted,
} from "@excalidraw/element/types";
import type { LocalPoint } from "@excalidraw/math";

import { AnimationController } from "./renderer/animation";

import type { Trail } from "./animatedTrail";
import type App from "./components/App";

export const ANNOTATION_FADE_MS = 320;

type AnnotationStroke = {
  element: NonDeleted<ExcalidrawFreeDrawElement>;
  points: LocalPoint[];
  pressures: number[];
  node: SVGPathElement;
  pathInvalidated: boolean;
  /** 0 while the pointer is down; pointer-up timestamp afterwards */
  releasedAt: number;
};

export class AnnotationTrail implements Trail {
  private strokes: AnnotationStroke[] = [];
  private current: AnnotationStroke | null = null;

  private container?: SVGSVGElement;
  private key: string;

  private static counter = 0;

  constructor(private app: App) {
    this.key = `annotation-trail-${AnnotationTrail.counter++}`;
  }

  get hasCurrentTrail() {
    return !!this.current;
  }

  start(container?: SVGSVGElement) {
    if (container) {
      this.container = container;
      for (const stroke of this.strokes) {
        if (stroke.node.parentNode !== container) {
          container.appendChild(stroke.node);
        }
      }
    }

    if (!AnimationController.running(this.key)) {
      AnimationController.start(this.key, () => {
        return this.onFrame() ? { keep: true } : null;
      });
    }
  }

  stop() {
    AnimationController.cancel(this.key);
    for (const stroke of this.strokes) {
      stroke.node.remove();
    }
    this.strokes = [];
    this.current = null;
  }

  startPath(x: number, y: number, pressure = 0.5, pointerType = "mouse") {
    if (this.current) {
      this.endPath();
    }

    const state = this.app.state;
    // Mirrors createFreeDrawElementOnPointerDown in App.tsx exactly — same
    // pressure simulation, same stroke options — so the overlay stroke is
    // the one a real freedraw element would produce.
    const simulatePressure = pressure === 0.5;
    const points = [pointFrom<LocalPoint>(0, 0)];
    const pressures = simulatePressure ? [] : [pressure];

    const element = newFreeDrawElement({
      type: "freedraw",
      x,
      y,
      strokeColor: state.currentItemStrokeColor,
      backgroundColor: state.currentItemBackgroundColor,
      fillStyle: state.currentItemFillStyle,
      strokeWidth: getStrokeWidthByKey(
        "freedraw",
        state.currentItemStrokeWidthKey,
      ),
      strokeStyle: state.currentItemStrokeStyle,
      roughness: state.currentItemRoughness,
      opacity: state.currentItemOpacity,
      roundness: null,
      simulatePressure,
      strokeOptions: {
        variability: state.currentItemStrokeVariability,
        streamline:
          pointerType !== "mouse"
            ? DEFAULT_STROKE_STREAMLINE_PRECISE
            : DEFAULT_STROKE_STREAMLINE,
      },
      locked: false,
      points,
      pressures,
    });

    const node = document.createElementNS(SVG_NS, "path");
    const stroke: AnnotationStroke = {
      element,
      points,
      pressures,
      node,
      pathInvalidated: true,
      releasedAt: 0,
    };
    this.container?.appendChild(node);
    this.strokes.push(stroke);
    this.current = stroke;

    this.start();
  }

  addPointToPath(x: number, y: number, pressure = 0.5) {
    const stroke = this.current;
    if (!stroke) {
      return;
    }
    const dx = x - stroke.element.x;
    const dy = y - stroke.element.y;
    const lastPoint = stroke.points[stroke.points.length - 1];
    if (lastPoint && lastPoint[0] === dx && lastPoint[1] === dy) {
      return;
    }
    stroke.points.push(pointFrom<LocalPoint>(dx, dy));
    if (!stroke.element.simulatePressure) {
      stroke.pressures.push(pressure);
    }
    stroke.pathInvalidated = true;

    this.start();
  }

  endPath() {
    if (this.current) {
      this.current.releasedAt = performance.now();
      this.current = null;
    }
  }

  /**
   * The path data currently rendered for the live stroke — exposed for the
   * render-parity test.
   */
  getCurrentPathData() {
    if (!this.current) {
      return null;
    }
    this.syncStrokePath(this.current);
    return this.current.node.getAttribute("d");
  }

  private syncStrokePath(stroke: AnnotationStroke) {
    if (stroke.pathInvalidated) {
      stroke.node.setAttribute(
        "d",
        getFreeDrawSvgPath({
          ...stroke.element,
          points: stroke.points,
          pressures: stroke.pressures,
        }),
      );
      stroke.pathInvalidated = false;
    }
  }

  private onFrame() {
    const state = this.app.state;
    const now = performance.now();

    for (const stroke of this.strokes.slice()) {
      let alpha = stroke.element.opacity / 100;
      if (stroke.releasedAt) {
        const t = (now - stroke.releasedAt) / ANNOTATION_FADE_MS;
        if (t >= 1) {
          stroke.node.remove();
          this.strokes.splice(this.strokes.indexOf(stroke), 1);
          continue;
        }
        alpha *= easeOut(1 - t);
      }

      this.syncStrokePath(stroke);

      // Same color transform the canvas renderer applies in dark mode
      // (element/src/renderElement.ts, freedraw case).
      stroke.node.setAttribute(
        "fill",
        applyDarkModeFilter(
          stroke.element.strokeColor,
          state.theme === THEME.DARK,
        ),
      );
      stroke.node.setAttribute("opacity", String(alpha));

      // Path data is in element-local scene units; position and scale it
      // into viewport coordinates like the canvas transform does.
      const { x: viewportX, y: viewportY } = sceneCoordsToViewportCoords(
        { sceneX: stroke.element.x, sceneY: stroke.element.y },
        state,
      );
      stroke.node.setAttribute(
        "transform",
        `translate(${viewportX} ${viewportY}) scale(${state.zoom.value})`,
      );
    }

    return this.strokes.length > 0;
  }
}
